/**
 * EKSTRE BİÇİMİNİ PAYLAŞ (kullanıcı kararı 21.10.2026): kurum ekstresinin
 * (PDF) düzenini, içeriğini göstermeden dosyaya çıkarır. Kurallar
 * `lib/ekstreMaske.ts`te; PDF okuma `lib/pdfMetin.ts`te.
 *
 * PDF yalnızca bu bileşenin belleğinde durur: kasaya yazılmaz, hiçbir yere
 * gönderilmez; "Temizle" ya da sayfadan çıkış onu unutur. Önizleme, inen
 * dosyanın `gorunum` alanıyla aynı işlevden gelir: kullanıcı ne görüyorsa o
 * paylaşılır.
 */

import { useMemo, useState } from "react";
import {
  kelimeAcikMi,
  kelimeleriSay,
  maskeDosyasi,
  sayfaGorunumu,
  type EkstreSayfasi,
  type KelimeSayimi,
  type MaskeSecimi,
} from "../lib/ekstreMaske";
import { M } from "../dil";
import { downloadBlob, readFileAsBytes } from "../lib/files";
import { pdfOkuyucu, type PdfOkumaSonucu } from "../lib/pdfMetin";
import { Button, Field, Notice, Select, Spinner } from "../ui/primitives";
import { GeriBildirimKarti } from "./GeriBildirimKarti";

export const MASKE_DOSYA_ADI = "parafon-ekstre-bicimi.json";

/** Listede ilk gösterilen kelime sayısı; gerisi "tümünü göster" ile. */
const ILK_GOSTERILEN = 40;

type Durum =
  | { readonly tur: "bos" }
  | { readonly tur: "okunuyor" }
  | { readonly tur: "sonuc"; readonly sonuc: PdfOkumaSonucu };

export function secimOf(degisen: ReadonlyMap<string, boolean>): MaskeSecimi {
  const acilan = new Set<string>();
  const kapatilan = new Set<string>();
  for (const [k, acik] of degisen) (acik ? acilan : kapatilan).add(k);
  return { acilan, kapatilan };
}

export function KelimeListesi({
  baslik,
  kelimeler,
  acik,
  onDegistir,
}: {
  readonly baslik: string;
  readonly kelimeler: readonly KelimeSayimi[];
  readonly acik: boolean;
  readonly onDegistir: (k: KelimeSayimi) => void;
}) {
  const [hepsi, setHepsi] = useState(false);
  const gosterilen = hepsi ? kelimeler : kelimeler.slice(0, ILK_GOSTERILEN);
  return (
    <div className="space-y-1">
      <p className="text-[12px] font-semibold text-ink">
        {baslik} · {kelimeler.length}
      </p>
      {kelimeler.length === 0 ? (
        <p className="text-[12px] text-faint">{M.iceAktar.maske.yok}</p>
      ) : (
        <ul className="flex flex-wrap gap-1" aria-label={baslik}>
          {gosterilen.map((k) => (
            <li key={k.anahtar}>
              <button
                type="button"
                aria-pressed={acik}
                title={acik ? M.iceAktar.maske.kapat : M.iceAktar.maske.ac}
                onClick={() => onDegistir(k)}
                className={
                  acik
                    ? "rounded-full border border-accent-border bg-accent-soft px-2 py-0.5 text-[12px] text-ink"
                    : "rounded-full border border-line px-2 py-0.5 text-[12px] text-muted"
                }
              >
                {k.yazilis}
              </button>
            </li>
          ))}
          {!hepsi && kelimeler.length > ILK_GOSTERILEN && (
            <li>
              <button type="button" className="px-2 py-0.5 text-[12px] text-accent" onClick={() => setHepsi(true)}>
                {M.iceAktar.maske.tumunuGoster}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function Onizleme({ sayfalar }: { readonly sayfalar: readonly EkstreSayfasi[] }) {
  const [degisen, setDegisen] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [sayfaNo, setSayfaNo] = useState(1);
  const secim = useMemo(() => secimOf(degisen), [degisen]);
  const kelimeler = useMemo(() => kelimeleriSay(sayfalar), [sayfalar]);
  const acik = kelimeler.filter((k) => kelimeAcikMi(k.yazilis, secim));
  const kapali = kelimeler.filter((k) => !kelimeAcikMi(k.yazilis, secim));
  const sayfa = sayfalar.find((s) => s.no === sayfaNo) ?? sayfalar[0]!;
  const dosya = useMemo(() => maskeDosyasi(sayfalar, secim), [sayfalar, secim]);
  const parcaSayisi = sayfalar.reduce((n, s) => n + s.parcalar.length, 0);

  const degistir = (k: KelimeSayimi, ac: boolean): void => {
    setDegisen((m) => {
      const yeni = new Map(m);
      if (ac === k.varsayilanAcik) yeni.delete(k.anahtar);
      else yeni.set(k.anahtar, ac);
      return yeni;
    });
  };

  return (
    <div className="min-w-0 space-y-3">
      <p className="text-[12px] text-muted">
        {M.iceAktar.maske.ozet(sayfalar.length, parcaSayisi)}
      </p>
      {sayfalar.length > 1 && (
        <Field label={M.iceAktar.maske.sayfa} htmlFor="ekstre-sayfa">
          <Select
            id="ekstre-sayfa"
            value={String(sayfa.no)}
            onChange={(e) => setSayfaNo(Number(e.currentTarget.value))}
            options={sayfalar.map((s) => ({ value: String(s.no), label: String(s.no) }))}
          />
        </Field>
      )}
      <pre
        aria-label={M.iceAktar.maske.onizleme}
        className="max-h-96 max-w-full overflow-auto rounded-pf border border-line p-2 font-mono text-[11px] leading-snug whitespace-pre text-ink"
      >
        {sayfaGorunumu(sayfa.parcalar, secim).join("\n")}
      </pre>
      <p className="text-[12px] text-muted">
        {M.iceAktar.maske.kelimeIpucu}
      </p>
      <KelimeListesi baslik={M.iceAktar.maske.acikKelimeler} kelimeler={acik} acik onDegistir={(k) => degistir(k, false)} />
      <KelimeListesi baslik={M.iceAktar.maske.kapaliKelimeler} kelimeler={kapali} acik={false} onDegistir={(k) => degistir(k, true)} />
      <Button
        variant="primary"
        onClick={() => downloadBlob(new Blob([dosya], { type: "application/json" }), MASKE_DOSYA_ADI)}
      >
        {M.iceAktar.maske.indir}
      </Button>
      <GeriBildirimKarti kaynak="ekstre-bicimi" metin={dosya} />
    </div>
  );
}

export function EkstreMaskeKarti() {
  const [durum, setDurum] = useState<Durum>({ tur: "bos" });
  const [veri, setVeri] = useState<Uint8Array | null>(null);
  const [parola, setParola] = useState("");
  // Her yeni okuma önizlemeyi (ve kelime seçimlerini) sıfırlar.
  const [okumaNo, setOkumaNo] = useState(0);

  const oku = async (bayt: Uint8Array, p?: string): Promise<void> => {
    setDurum({ tur: "okunuyor" });
    let sonuc: PdfOkumaSonucu;
    try {
      sonuc = await pdfOkuyucu.oku(bayt, p);
    } catch (e) {
      sonuc = { tur: "okunamadi", sebep: e instanceof Error ? e.message : String(e) };
    }
    if (sonuc.tur !== "parola") setParola("");
    if (sonuc.tur === "tamam") setVeri(null); // metin çıktı; PDF'in kendisi unutulur
    setOkumaNo((n) => n + 1);
    setDurum({ tur: "sonuc", sonuc });
  };

  const temizle = (): void => {
    setVeri(null);
    setParola("");
    setDurum({ tur: "bos" });
  };

  const sonuc = durum.tur === "sonuc" ? durum.sonuc : null;

  return (
    <section className="pf-card min-w-0 space-y-3 p-4" aria-labelledby="ekstre-maske">
      <h2 id="ekstre-maske" className="text-[14px] font-semibold text-ink">
        {M.iceAktar.maske.baslik}
      </h2>
      <p className="text-[12px] text-muted">
        {M.iceAktar.maske.aciklama}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="pf-btn pf-btn-ghost cursor-pointer">
          {M.iceAktar.maske.pdfSec}
          <input
            type="file"
            accept=".pdf,application/pdf"
            className="sr-only"
            aria-label={M.iceAktar.maske.pdfEtiket}
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              e.currentTarget.value = "";
              if (f === undefined) return;
              void readFileAsBytes(f).then((b) => {
                setVeri(b);
                return oku(b);
              });
            }}
          />
        </label>
        {durum.tur !== "bos" && <Button onClick={temizle}>{M.iceAktar.maske.temizle}</Button>}
      </div>

      {durum.tur === "okunuyor" && <Spinner label={M.iceAktar.maske.okunuyor} />}

      {sonuc?.tur === "parola" && veri !== null && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void oku(veri, parola);
          }}
        >
          <Field
            label={M.iceAktar.maske.parola}
            htmlFor="ekstre-parola"
            hint={M.iceAktar.maske.parolaIpucu}
            {...(sonuc.yanlis ? { error: M.iceAktar.maske.parolaTutmadi } : {})}
          >
            <input
              id="ekstre-parola"
              type="password"
              autoComplete="off"
              className="pf-input"
              value={parola}
              onChange={(e) => setParola(e.currentTarget.value)}
            />
          </Field>
          <Button type="submit" variant="primary" disabled={parola === ""}>
            {M.iceAktar.maske.ac}
          </Button>
        </form>
      )}

      {sonuc?.tur === "metin-yok" && (
        <Notice tone="warn" title={M.iceAktar.maske.metinYokBaslik}>
          <p>
            {M.iceAktar.maske.metinYok(sonuc.sayfaSayisi)}
          </p>
        </Notice>
      )}

      {sonuc?.tur === "okunamadi" && (
        <Notice tone="danger" title={M.iceAktar.maske.okunamadi}>
          <p>{sonuc.sebep}</p>
        </Notice>
      )}

      {sonuc?.tur === "tamam" && <Onizleme key={okumaNo} sayfalar={sonuc.sayfalar} />}
    </section>
  );
}
